import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Briefcase, MapPin, Clock, DollarSign, Search, Filter, Building2,
  ArrowLeft, ChevronRight, ChevronDown, ExternalLink, Loader, Calendar, X, Star, MessageSquare,
  SlidersHorizontal, Sparkles, FileText, Upload, Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const jobTypeLabels = { internship: 'Internship', full_time: 'Full-time', contract: 'Contract' };
const workModeLabels = { remote: 'Remote', hybrid: 'Hybrid', onsite: 'On-site' };
const experienceLevelLabels = { fresher: 'Fresher', '1_3_years': '1-3 yrs', '3_5_years': '3-5 yrs', '5_plus_years': '5+ yrs' };

const CodeArenaJobsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  // Resume Analysis
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisJob, setAnalysisJob] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [uploadingResume, setUploadingResume] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    job_type: searchParams.get('job_type') || '',
    work_mode: searchParams.get('work_mode') || '',
    experience_level: searchParams.get('experience_level') || '',
    skill: searchParams.get('skill') || ''
  });

  const token = localStorage.getItem('codearena_token');

  useEffect(() => { fetchJobs(); }, [page, filters]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '12');
      if (filters.search) params.set('search', filters.search);
      if (filters.job_type) params.set('job_type', filters.job_type);
      if (filters.work_mode) params.set('work_mode', filters.work_mode);
      if (filters.experience_level) params.set('experience_level', filters.experience_level);
      if (filters.skill) params.set('skill', filters.skill);
      const res = await axios.get(`${API}/codearena/jobs?${params.toString()}`);
      setJobs(res.data.jobs || []);
      setTotalJobs(res.data.total || 0);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  const applyFilters = () => {
    setPage(1);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
    setSearchParams(params);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ search: '', job_type: '', work_mode: '', experience_level: '', skill: '' });
    setPage(1);
    setSearchParams({});
  };

  const formatSalary = (min, max, currency = 'INR') => {
    if (!min && !max) return 'Not disclosed';
    const fmt = (n) => n >= 100000 ? `${(n / 100000).toFixed(1)}L` : n?.toLocaleString('en-IN');
    if (min && max) return `${fmt(min)} - ${fmt(max)}`;
    if (min) return `${fmt(min)}+`;
    return `Up to ${fmt(max)}`;
  };

  const formatTimeAgo = (dateStr) => {
    const days = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1d ago';
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Only PDF files supported'); return; }
    setUploadingResume(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/mock-interview/upload-resume`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setResumeText(res.data.resume_text || '');
      toast.success('Resume text extracted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process resume');
    } finally { setUploadingResume(false); }
  };

  const runAnalysis = async () => {
    if (!resumeText.trim() || !analysisJob) return;
    setAnalyzing(true);
    try {
      const jdText = `${analysisJob.title} at ${analysisJob.company_name}\n\n${analysisJob.description || ''}${analysisJob.skills_required?.length ? `\n\nRequired Skills: ${analysisJob.skills_required.join(', ')}` : ''}`;
      const res = await axios.post(`${API}/mock-interview/resume-analysis`, {
        resume_text: resumeText,
        job_description: jdText
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 45000 });
      setAnalysisResult(res.data.analysis);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally { setAnalyzing(false); }
  };

  const activeFilterCount = Object.values(filters).filter(v => v).length;

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Top Bar */}
      <header className="sticky top-0 z-50 border-b" style={{ background: '#fff', borderColor: '#E4E4E7' }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <Link to="/codearena" className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors text-sm font-medium" data-testid="back-link">
                <ArrowLeft size={16} />
                <span className="hidden sm:inline">Arena</span>
              </Link>
              <div className="w-px h-5 bg-zinc-200" />
              <h1 className="text-base font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }} data-testid="jobs-page-title">
                Job Board
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-zinc-400" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                {totalJobs} positions
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-6">
        {/* Search & Filter Bar */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
            <input type="text" placeholder="Search roles, companies, skills..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              data-testid="job-search-input"
              className="w-full pl-10 pr-4 py-2.5 text-sm border bg-white focus:outline-none focus:ring-1 transition-all"
              style={{ borderColor: '#E4E4E7', borderRadius: '2px', fontFamily: "'IBM Plex Sans', sans-serif" }}
              onFocus={e => { e.target.style.borderColor = '#002FA7'; e.target.style.boxShadow = '0 0 0 1px #002FA7'; }}
              onBlur={e => { e.target.style.borderColor = '#E4E4E7'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn"
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border transition-all"
              style={{
                borderRadius: '2px',
                borderColor: showFilters || activeFilterCount > 0 ? '#002FA7' : '#E4E4E7',
                background: showFilters || activeFilterCount > 0 ? '#002FA7' : '#fff',
                color: showFilters || activeFilterCount > 0 ? '#fff' : '#52525B'
              }}>
              <SlidersHorizontal size={14} />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-1 w-4 h-4 text-[10px] font-bold flex items-center justify-center"
                  style={{ background: 'rgba(255,255,255,0.2)', borderRadius: '1px' }}>
                  {activeFilterCount}
                </span>
              )}
            </button>
            <button onClick={applyFilters} data-testid="search-btn"
              className="px-5 py-2.5 text-sm font-semibold tracking-wide transition-all"
              style={{ background: '#002FA7', color: '#fff', borderRadius: '2px' }}
              onMouseEnter={e => e.target.style.background = '#00227A'}
              onMouseLeave={e => e.target.style.background = '#002FA7'}>
              Search
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mb-6 p-5 border bg-white" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }} data-testid="filter-panel">
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { key: 'job_type', label: 'TYPE', options: [['', 'All'], ['internship', 'Internship'], ['full_time', 'Full-time'], ['contract', 'Contract']] },
                { key: 'work_mode', label: 'MODE', options: [['', 'All'], ['remote', 'Remote'], ['hybrid', 'Hybrid'], ['onsite', 'On-site']] },
                { key: 'experience_level', label: 'EXPERIENCE', options: [['', 'All'], ['fresher', 'Fresher'], ['1_3_years', '1-3 yrs'], ['3_5_years', '3-5 yrs'], ['5_plus_years', '5+']] },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-[10px] font-bold tracking-[0.1em] text-zinc-400 mb-2 uppercase">{f.label}</label>
                  <select value={filters[f.key]} onChange={e => setFilters(prev => ({ ...prev, [f.key]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border bg-white focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
                    style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                    {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              ))}
              <div>
                <label className="block text-[10px] font-bold tracking-[0.1em] text-zinc-400 mb-2 uppercase">SKILL</label>
                <input type="text" value={filters.skill} onChange={e => setFilters(prev => ({ ...prev, skill: e.target.value }))}
                  placeholder="React, Python..." className="w-full px-3 py-2 text-sm border bg-white focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7]"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px' }} />
              </div>
            </div>
            <div className="flex justify-between mt-4 pt-3 border-t" style={{ borderColor: '#E4E4E7' }}>
              <button onClick={clearFilters} className="text-zinc-500 hover:text-zinc-900 text-xs font-medium">Clear all</button>
              <button onClick={applyFilters} className="px-4 py-1.5 text-xs font-bold tracking-wide text-white"
                style={{ background: '#002FA7', borderRadius: '2px' }}>Apply</button>
            </div>
          </div>
        )}

        {/* Job List */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader className="animate-spin text-[#002FA7]" size={28} /></div>
        ) : jobs.length > 0 ? (
          <div className="space-y-2">
            {jobs.map((job, idx) => (
              <div key={job.id} className="group bg-white border hover:shadow-sm transition-all" data-testid={`job-card-${idx}`}
                style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                <div className="flex items-start gap-5 p-5">
                  {/* Company Initial */}
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center text-sm font-bold"
                    style={{ background: '#F4F4F5', color: '#002FA7', borderRadius: '2px', fontFamily: "'Space Grotesk', sans-serif" }}>
                    {job.company_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Row 1: Title + Meta */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <Link to={`/codearena/jobs/${job.id}`} data-testid={`job-title-${idx}`}
                          className="text-sm font-bold text-[#0A0A0A] group-hover:text-[#002FA7] transition-colors block truncate"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                          {job.title}
                        </Link>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-xs font-medium text-zinc-500">{job.company_name}</span>
                          {job.is_featured && (
                            <span className="text-[9px] font-bold tracking-[0.1em] uppercase px-1.5 py-0.5"
                              style={{ background: '#FFD600', color: '#0A0A0A', borderRadius: '1px' }}>
                              FEATURED
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="shrink-0 text-[10px] font-medium text-zinc-400" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {formatTimeAgo(job.created_at)}
                      </span>
                    </div>

                    {/* Row 2: Tags */}
                    <div className="flex flex-wrap items-center gap-2 mt-2.5">
                      <span className="text-[10px] font-bold tracking-[0.08em] uppercase px-2 py-0.5 border"
                        style={{ borderColor: '#E4E4E7', color: '#002FA7', background: '#002FA710', borderRadius: '1px' }}>
                        {jobTypeLabels[job.job_type] || job.job_type}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-zinc-500">
                        <MapPin size={10} /> {job.location || 'N/A'}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-zinc-500">
                        <Briefcase size={10} /> {workModeLabels[job.work_mode] || 'N/A'}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-zinc-500">
                        <Clock size={10} /> {experienceLevelLabels[job.experience_level] || 'N/A'}
                      </span>
                      <span className="flex items-center gap-1 text-[11px] font-medium text-zinc-600"
                        style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                      </span>
                    </div>

                    {/* Row 3: Skills */}
                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                      {job.skills_required?.slice(0, 5).map((skill, si) => (
                        <span key={si} className="text-[10px] font-medium px-2 py-0.5"
                          style={{ background: '#F4F4F5', color: '#52525B', borderRadius: '1px' }}>
                          {skill}
                        </span>
                      ))}
                      {job.skills_required?.length > 5 && (
                        <span className="text-[10px] font-medium px-2 py-0.5"
                          style={{ background: '#F4F4F5', color: '#A1A1AA', borderRadius: '1px' }}>
                          +{job.skills_required.length - 5}
                        </span>
                      )}
                    </div>

                    {/* Row 4: Actions */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: '#F4F4F5' }}>
                      <Link to={`/codearena/jobs/${job.id}`} data-testid={`view-job-${idx}`}
                        className="px-3.5 py-1.5 text-[11px] font-bold tracking-wide text-white transition-all"
                        style={{ background: '#002FA7', borderRadius: '2px' }}
                        onMouseEnter={e => e.target.style.background = '#00227A'}
                        onMouseLeave={e => e.target.style.background = '#002FA7'}>
                        View & Apply
                      </Link>
                      <button onClick={(e) => {
                          e.preventDefault();
                          navigate('/codearena/mock-interview', {
                            state: { job_title: job.title, company_name: job.company_name,
                              job_description: `${job.title} at ${job.company_name}\n\n${job.description}${job.skills_required?.length ? `\n\nSkills: ${job.skills_required.join(', ')}` : ''}` }
                          });
                        }}
                        data-testid={`practice-btn-${idx}`}
                        className="px-3.5 py-1.5 text-[11px] font-bold tracking-wide border transition-all flex items-center gap-1.5"
                        style={{ borderColor: '#059669', color: '#059669', background: '#05966910', borderRadius: '2px' }}>
                        <MessageSquare size={11} /> Practice
                      </button>
                      {token && (
                        <button onClick={() => { setAnalysisJob(job); setShowAnalysis(true); setAnalysisResult(null); }}
                          data-testid={`resume-analysis-btn-${idx}`}
                          className="px-3.5 py-1.5 text-[11px] font-bold tracking-wide border transition-all flex items-center gap-1.5"
                          style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}
                          onMouseEnter={e => { e.target.style.borderColor = '#002FA7'; e.target.style.color = '#002FA7'; }}
                          onMouseLeave={e => { e.target.style.borderColor = '#E4E4E7'; e.target.style.color = '#52525B'; }}>
                          <Sparkles size={11} /> Resume Match
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Pagination */}
            {totalJobs > 12 && (
              <div className="flex items-center justify-between pt-6">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-4 py-2 text-xs font-bold border disabled:opacity-30 transition-all"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                  Previous
                </button>
                <span className="text-xs text-zinc-400" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  {page} / {Math.ceil(totalJobs / 12)}
                </span>
                <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(totalJobs / 12)}
                  className="px-4 py-2 text-xs font-bold border disabled:opacity-30 transition-all"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                  Next
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white border p-16 text-center" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
            <Briefcase className="mx-auto text-zinc-300 mb-4" size={40} />
            <h3 className="font-bold text-[#0A0A0A] mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>No positions found</h3>
            <p className="text-zinc-500 text-sm mb-4">Try adjusting your search or filters</p>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="px-5 py-2 text-xs font-bold tracking-wide text-white"
                style={{ background: '#002FA7', borderRadius: '2px' }}>Clear Filters</button>
            )}
          </div>
        )}
      </div>

      {/* Resume Analysis Modal */}
      {showAnalysis && analysisJob && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto border" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 24px 48px rgba(0,0,0,0.12)' }}
            data-testid="resume-analysis-modal">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: '#E4E4E7' }}>
              <div>
                <h3 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  Resume Analysis
                </h3>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Match your resume against <strong>{analysisJob.title}</strong> at {analysisJob.company_name}
                </p>
              </div>
              <button onClick={() => { setShowAnalysis(false); setAnalysisResult(null); }}
                className="p-1 text-zinc-400 hover:text-zinc-700 transition-colors" data-testid="close-analysis-modal">
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {!analysisResult ? (
                <>
                  {/* Upload Resume */}
                  <div>
                    <label className="block text-[10px] font-bold tracking-[0.1em] text-zinc-400 mb-2 uppercase">Your Resume</label>
                    <div className="flex gap-3">
                      <label className="flex-1 flex items-center justify-center gap-2 py-3 border-2 border-dashed cursor-pointer transition-all"
                        style={{ borderColor: resumeText ? '#059669' : '#E4E4E7', background: resumeText ? '#05966908' : '#fff', borderRadius: '2px' }}>
                        <input type="file" accept=".pdf" className="hidden" onChange={handleResumeUpload} data-testid="resume-upload-input" />
                        {uploadingResume ? (
                          <><Loader2 size={14} className="animate-spin text-zinc-500" /> <span className="text-xs text-zinc-500">Processing...</span></>
                        ) : resumeText ? (
                          <><FileText size={14} className="text-[#059669]" /> <span className="text-xs font-medium text-[#059669]">Resume loaded ({resumeText.length} chars)</span></>
                        ) : (
                          <><Upload size={14} className="text-zinc-400" /> <span className="text-xs text-zinc-500">Upload PDF Resume</span></>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Or paste resume text */}
                  <div>
                    <label className="block text-[10px] font-bold tracking-[0.1em] text-zinc-400 mb-2 uppercase">Or paste resume text</label>
                    <textarea value={resumeText} onChange={e => setResumeText(e.target.value)} rows={5}
                      data-testid="resume-text-input"
                      placeholder="Paste your resume content here..."
                      className="w-full px-3 py-2.5 text-sm border bg-white focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] resize-none"
                      style={{ borderColor: '#E4E4E7', borderRadius: '2px', fontFamily: "'IBM Plex Sans', sans-serif" }} />
                  </div>

                  {/* Analyze Button */}
                  <button onClick={runAnalysis} disabled={!resumeText.trim() || analyzing}
                    data-testid="run-analysis-btn"
                    className="w-full flex items-center justify-center gap-2 py-3 text-sm font-bold tracking-wide text-white transition-all disabled:opacity-50"
                    style={{ background: '#002FA7', borderRadius: '2px' }}>
                    {analyzing ? <><Loader2 size={14} className="animate-spin" /> Analyzing...</> : <><Sparkles size={14} /> Analyze Match</>}
                  </button>
                </>
              ) : (
                /* Analysis Results */
                <div className="space-y-5" data-testid="analysis-results">
                  {/* Match Score */}
                  <div className="text-center py-4">
                    <div className="text-5xl font-black" data-testid="match-score"
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        color: analysisResult.match_score >= 70 ? '#059669' : analysisResult.match_score >= 40 ? '#D97706' : '#DC2626'
                      }}>
                      {analysisResult.match_score}%
                    </div>
                    <p className="text-xs font-bold tracking-[0.1em] uppercase mt-1"
                      style={{ color: analysisResult.match_score >= 70 ? '#059669' : analysisResult.match_score >= 40 ? '#D97706' : '#DC2626' }}>
                      {analysisResult.match_score >= 70 ? 'STRONG MATCH' : analysisResult.match_score >= 40 ? 'PARTIAL MATCH' : 'LOW MATCH'}
                    </p>
                  </div>

                  {/* Summary */}
                  {analysisResult.summary && (
                    <div className="p-4 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                      <p className="text-sm text-zinc-700 leading-relaxed">{analysisResult.summary}</p>
                    </div>
                  )}

                  {/* Missing Skills */}
                  {analysisResult.missing_skills?.length > 0 && (
                    <div>
                      <h4 className="text-[10px] font-bold tracking-[0.1em] text-zinc-400 uppercase mb-2">Missing Skills</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {analysisResult.missing_skills.map((s, i) => (
                          <span key={i} className="text-[11px] font-medium px-2.5 py-1 border" data-testid={`missing-skill-${i}`}
                            style={{ borderColor: '#FF336630', color: '#DC2626', background: '#FF336608', borderRadius: '1px' }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Matching Skills */}
                  {analysisResult.matching_skills?.length > 0 && (
                    <div>
                      <h4 className="text-[10px] font-bold tracking-[0.1em] text-zinc-400 uppercase mb-2">Matching Skills</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {analysisResult.matching_skills.map((s, i) => (
                          <span key={i} className="text-[11px] font-medium px-2.5 py-1 border"
                            style={{ borderColor: '#05966930', color: '#059669', background: '#05966908', borderRadius: '1px' }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Suggestions */}
                  {analysisResult.suggestions?.length > 0 && (
                    <div>
                      <h4 className="text-[10px] font-bold tracking-[0.1em] text-zinc-400 uppercase mb-2">Improvement Suggestions</h4>
                      <div className="space-y-2">
                        {analysisResult.suggestions.map((s, i) => (
                          <div key={i} className="flex gap-2.5 p-3 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}
                            data-testid={`suggestion-${i}`}>
                            <div className="w-5 h-5 shrink-0 flex items-center justify-center text-[10px] font-bold"
                              style={{ background: '#002FA710', color: '#002FA7', borderRadius: '1px' }}>
                              {i + 1}
                            </div>
                            <p className="text-xs text-zinc-600 leading-relaxed">{s}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <button onClick={() => setAnalysisResult(null)} data-testid="reanalyze-btn"
                      className="flex-1 py-2.5 text-xs font-bold border transition-all"
                      style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                      Try Again
                    </button>
                    <button onClick={() => {
                        navigate('/codearena/mock-interview', {
                          state: { job_title: analysisJob.title, company_name: analysisJob.company_name,
                            job_description: `${analysisJob.title} at ${analysisJob.company_name}\n\n${analysisJob.description}` }
                        });
                      }} data-testid="practice-from-analysis"
                      className="flex-1 py-2.5 text-xs font-bold text-white transition-all flex items-center justify-center gap-1.5"
                      style={{ background: '#059669', borderRadius: '2px' }}>
                      <MessageSquare size={12} /> Practice Interview
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeArenaJobsPage;
